/**
 * Swap or Pickup Step
 *
 * Phase 2 step that appears after container lookup, presenting clear options
 * for what the customer wants to do with their existing container.
 *
 * User Story:
 * "As a customer with a container on-site, I want to clearly understand the
 * difference between swap and pickup so I can choose the right service"
 */

import { useFormContext } from 'react-hook-form';
import { ActionCard, type ActionOption } from '../../../components/order/ActionCard';
import { ContainerSummary } from '../../../components/order/ContainerSummary';
import { Card, CardContent } from '../../../components/ui/card';
import { InfoIcon } from 'lucide-react';
import type { OrderForm } from '../../../schemas/order.schema';

const SWAP_PICKUP_OPTIONS: ActionOption[] = [
  {
    id: 'swap',
    icon: '🔄',
    title: 'Swap it out',
    description: "We'll bring an empty container and take your full one",
    popular: true,
    estimatedTime: 'Next business day',
  },
  {
    id: 'pickup',
    icon: '🚚',
    title: 'Pick it up',
    description: "We'll remove your container and dispose of contents",
    estimatedTime: '1-2 business days',
  },
];

export function SwapOrPickupStep() {
  const { setValue, watch } = useFormContext<OrderForm>();

  // Watch form values
  const existingOrderId = watch('existingOrderId');
  const selectedAction = watch('orderType');

  const handleActionSelect = (actionId: string) => {
    setValue('orderType', actionId as 'swap' | 'pickup');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          What would you like to do?
        </h2>
        <p className="text-sm text-gray-600">
          Choose the service that best fits your needs
        </p>
      </div>

      {/* Container context */}
      <ContainerSummary orderId={existingOrderId} />

      {/* Action selection */}
      <div className="grid gap-4 md:grid-cols-2">
        {SWAP_PICKUP_OPTIONS.map((option) => (
          <ActionCard
            key={option.id}
            option={option}
            selected={selectedAction === option.id}
            onSelect={() => handleActionSelect(option.id)}
          />
        ))}
      </div>

      {/* Service comparison / educational content */}
      <ServiceComparison />
    </div>
  );
}

/**
 * Service Comparison Component
 *
 * Helps customers understand the differences between swap and pickup
 * to make an informed decision.
 */
function ServiceComparison() {
  return (
    <Card className="bg-gray-50 border-gray-200">
      <CardContent className="p-6">
        <div className="flex items-start gap-3 mb-4">
          <InfoIcon className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">
              What&apos;s the difference?
            </h3>
            <p className="text-sm text-gray-600">
              Choose based on whether you need the container replaced or completely removed.
            </p>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {/* Swap details */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🔄</span>
              <h4 className="font-semibold text-gray-900">Swap</h4>
            </div>
            <ul className="text-sm text-gray-700 space-y-1 ml-8">
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-1">✓</span>
                <span>Keep a container on-site</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-1">✓</span>
                <span>Great for ongoing projects</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-1">✓</span>
                <span>Container must be at least 50% full</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-1">✓</span>
                <span>Same container size or different</span>
              </li>
            </ul>
          </div>

          {/* Pickup details */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🚚</span>
              <h4 className="font-semibold text-gray-900">Pickup</h4>
            </div>
            <ul className="text-sm text-gray-700 space-y-1 ml-8">
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-1">✓</span>
                <span>Complete container removal</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-1">✓</span>
                <span>Best for completed projects</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-1">✓</span>
                <span>No minimum fill requirement</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-1">✓</span>
                <span>Flexible scheduling</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Additional help */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-600 text-center">
            Still not sure?{' '}
            <button
              type="button"
              className="text-primary hover:underline focus:outline-none focus:underline"
              onClick={() => {
                // This would open a help modal or chat
                console.log('Open help');
              }}
            >
              Chat with our team
            </button>
            {' '}for personalized recommendations
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
